import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

/**
 * Rolling flash-news ticker for the top navbar.
 * Only items returned by /flash-news/active (audience + schedule) are shown.
 */
export default function NavbarFlashMarquee({ darkMode = false }) {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const res = await api.get("/global-settings/flash-news/active");
      const rows = res?.data?.data;
      setItems(Array.isArray(rows) ? rows : []);
    } catch {
      // Fallback: evaluate localStorage items for this session (dev / offline)
      try {
        const stored = localStorage.getItem("flashNews");
        const list = stored ? JSON.parse(stored) : [];
        const now = Date.now();
        const user = (() => {
          try {
            return JSON.parse(localStorage.getItem("currentUser") || "null");
          } catch {
            return null;
          }
        })();
        const employeeId = Number(user?.employeeId || user?.employee?.id);
        const userId = Number(user?.id || user?.userId);
        const active = (Array.isArray(list) ? list : []).filter((item) => {
          if (String(item.status || "active").toLowerCase() !== "active") return false;
          const from = item.displayFrom ? new Date(item.displayFrom).getTime() : NaN;
          const until = item.displayUntil ? new Date(item.displayUntil).getTime() : NaN;
          if (!Number.isFinite(from) || !Number.isFinite(until) || now < from || now > until) {
            return false;
          }
          const assignment = String(item.assignmentType || "all").toLowerCase();
          if (assignment === "all") return true;
          if (assignment !== "filter") return false;
          const ids = (item.filterData?.employeesData || [])
            .map((e) => Number(e?.id ?? e?.employeeId))
            .filter((n) => Number.isFinite(n));
          const userIds = (item.filterData?.employeesData || [])
            .map((e) => Number(e?.userId ?? e?.user_id))
            .filter((n) => Number.isFinite(n));
          return (
            (employeeId && ids.includes(employeeId)) ||
            (userId && userIds.includes(userId))
          );
        });
        setItems(
          active.map((i) => ({
            id: i.id,
            title: i.title,
            content: i.content,
            displayUntil: i.displayUntil,
          }))
        );
      } catch {
        setItems([]);
      }
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("pace-flash-news-updated", load);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pace-flash-news-updated", load);
    };
  }, []);

  const tickerText = useMemo(() => {
    if (!items.length) return "";
    return items
      .map((i) => {
        const title = String(i.title || "").trim();
        const content = String(i.content || "").trim();
        if (title && content) return `${title} — ${content}`;
        return title || content;
      })
      .filter(Boolean)
      .join("   •   ");
  }, [items]);

  if (!tickerText) return null;

  const durationSec = Math.max(18, Math.min(60, Math.ceil(tickerText.length / 6)));

  return (
    <div
      className={`pointer-events-none absolute inset-y-2 left-[min(14rem,28vw)] right-[min(16rem,34vw)] z-[5] overflow-hidden rounded-full border px-3 ${
        darkMode
          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-warning)/0.12)]"
          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-warning)/0.14)]"
      }`}
      aria-live="polite"
      title={tickerText}
    >
      <div className="flex h-full items-center overflow-hidden">
        <div
          className="pace-flash-marquee flex whitespace-nowrap text-xs font-semibold sm:text-sm"
          style={{ animationDuration: `${durationSec}s` }}
        >
          <span
            className={`inline-flex items-center gap-6 pr-12 ${
              darkMode ? "text-[hsl(var(--ds-warning))]" : "text-[hsl(var(--ds-warning))]"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <span className="text-[hsl(var(--ds-primary))]" aria-hidden>
                ✦
              </span>
              {tickerText}
            </span>
            <span className="inline-flex items-center gap-2" aria-hidden>
              <span className="text-[hsl(var(--ds-primary))]">✦</span>
              {tickerText}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
